import { USER_ROLE, APPROVER_TAG } from '../../../../utils/constants.js';

function toUserIdString(raw) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'object') {
    const id = raw._id || raw.id || raw.user_id;
    return id != null && id !== '' ? String(id) : '';
  }
  return String(raw);
}

export function normalizeUserIdList(users) {
  if (!Array.isArray(users)) return [];
  const seen = new Set();
  const out = [];
  users.forEach((u) => {
    const id = toUserIdString(u);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

function withoutIds(list = [], exclude = new Set()) {
  return normalizeUserIdList(list).filter((id) => !exclude.has(id));
}

/**
 * Users that must be hidden from a role picker (SOD). Approver vs Sign-Off
 * are mutually exclusive in the menu so existing chips are not auto-cleared.
 * Prefers `user_roles` when present; falls back to flat preparer / approver fields.
 */
export function excludeIdsForAssignmentRole(assignment = {}, field) {
  const roles = Array.isArray(assignment.user_roles) ? assignment.user_roles : null;
  const preparer = roles
    ? activeUserIdsFromUserRoles(roles, USER_ROLE.PREPARER)
    : normalizeUserIdList(assignment.preparer);
  const approver = roles
    ? activeUserIdsFromUserRoles(roles, USER_ROLE.APPROVER, APPROVER_TAG.L1)
    : normalizeUserIdList(assignment.approver);
  const signOff = roles
    ? activeUserIdsFromUserRoles(roles, USER_ROLE.APPROVER, APPROVER_TAG.L2)
    : normalizeUserIdList(assignment.sign_off_approvers);
  if (field === USER_ROLE.PREPARER) return [...approver, ...signOff];
  if (field === USER_ROLE.APPROVER) return [...preparer, ...signOff];
  if (field === USER_ROLE.SIGN_OFF_APPROVERS) return [...preparer, ...approver];
  return [];
}

/**
 * When a user is assigned as preparer, drop them from Approver / Sign-Off.
 * Approver vs Sign-Off overlap is blocked in the picker (exclude ids) so
 * changing one role must not silently clear the other.
 */
export function syncAssignmentRolesOnChange(assignment = {}, field, rawValue) {
  const ids = normalizeUserIdList(rawValue);
  const idSet = new Set(ids);
  const next = {
    ...assignment,
    preparer: normalizeUserIdList(assignment.preparer),
    approver: normalizeUserIdList(assignment.approver),
    sign_off_approvers: normalizeUserIdList(assignment.sign_off_approvers),
    requires_sign_off: !!assignment.requires_sign_off,
  };

  if (field === USER_ROLE.PREPARER) {
    next.preparer = ids;
    next.approver = withoutIds(next.approver, idSet);
    next.sign_off_approvers = withoutIds(next.sign_off_approvers, idSet);
  } else if (field === USER_ROLE.APPROVER) {
    next.approver = ids;
    next.preparer = withoutIds(next.preparer, idSet);
  } else if (field === USER_ROLE.SIGN_OFF_APPROVERS) {
    next.sign_off_approvers = ids;
    next.preparer = withoutIds(next.preparer, idSet);
    next.requires_sign_off = ids.length > 0 ? true : !!next.requires_sign_off;
  } else {
    next[field] = rawValue;
  }

  if (!next.requires_sign_off) {
    next.sign_off_approvers = [];
  }

  next.user_roles = buildCycleUserRolesFromAssignments(next);
  return next;
}

/**
 * Active user IDs from location[].user_roles[] by role + optional tag.
 */
export function activeUserIdsFromUserRoles(userRoles = [], role, tag = null) {
  if (!Array.isArray(userRoles)) return [];
  const seen = new Set();
  const out = [];

  userRoles.forEach((entry) => {
    if (!entry || entry.status === false) return;
    if (entry.role !== role) return;
    if (tag != null && entry.tag !== tag) return;
    const id = toUserIdString(entry.user_id || entry);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });

  return out;
}

/**
 * Build cycle-task `user_roles[]` from flat preparer / approver / sign-off ids.
 * Matches Supernova `buildUserRolesFromLegacyArrays` + cycle `actions_performed: []`.
 * Send alongside flat fields on validate / create / edit so access checks use current assignees.
 *
 * Entry shape: `{ user_id, role: 'preparer'|'approver', tag: null|'L1'|'L2', status: true, actions_performed: [] }`
 */
export function buildCycleUserRolesFromAssignments({
  preparer = [],
  approver = [],
  sign_off_approvers = [],
  sign_off_approver = [],
} = {}) {
  const byUser = new Map();
  const l2Source =
    Array.isArray(sign_off_approvers) && sign_off_approvers.length
      ? sign_off_approvers
      : sign_off_approver || [];

  const upsert = (raw, role, tag) => {
    const id = toUserIdString(raw);
    if (!id) return;
    byUser.set(id, {
      user_id: id,
      role,
      tag,
      status: true,
      actions_performed: [],
    });
  };

  normalizeUserIdList(preparer).forEach((id) => upsert(id, USER_ROLE.PREPARER, null));
  normalizeUserIdList(approver).forEach((id) => upsert(id, USER_ROLE.APPROVER, APPROVER_TAG.L1));
  normalizeUserIdList(l2Source).forEach((id) => upsert(id, USER_ROLE.APPROVER, APPROVER_TAG.L2));

  return [...byUser.values()];
}

/**
 * Clone a cycle task / bulk row payload with `user_roles` built from current flat assignees.
 * @param {object} payload
 * @returns {object}
 */
export function withCycleUserRoles(payload = {}) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;
  return {
    ...payload,
    user_roles: buildCycleUserRolesFromAssignments(payload),
  };
}

/**
 * Hydrate flat assignment role fields from one location[] row (user_roles first, legacy fallback).
 */
export function assignmentRolesFromLocation(loc = {}) {
  const userRoles = loc?.user_roles;
  if (Array.isArray(userRoles) && userRoles.length) {
    const signOffApprovers = activeUserIdsFromUserRoles(
      userRoles,
      USER_ROLE.APPROVER,
      APPROVER_TAG.L2
    );
    const requiresSignOff =
      loc.requires_sign_off != null
        ? !!loc.requires_sign_off
        : signOffApprovers.length > 0;

    return {
      preparer: activeUserIdsFromUserRoles(userRoles, USER_ROLE.PREPARER),
      approver: activeUserIdsFromUserRoles(userRoles, USER_ROLE.APPROVER, APPROVER_TAG.L1),
      sign_off_approvers: signOffApprovers,
      requires_sign_off: requiresSignOff,
      auto_approve: loc?.auto_approve != null ? !!loc.auto_approve : false,
    };
  }

  const legacySignOff = loc?.sign_off_approvers || loc?.sign_off_approver || [];
  const signOffApprovers = normalizeUserIdList(legacySignOff);
  const requiresSignOff =
    loc.requires_sign_off != null ? !!loc.requires_sign_off : signOffApprovers.length > 0;

  return {
    preparer: normalizeUserIdList(loc?.preparer),
    approver: normalizeUserIdList(loc?.approver),
    sign_off_approvers: signOffApprovers,
    requires_sign_off: requiresSignOff,
    auto_approve: loc?.auto_approve != null ? !!loc.auto_approve : false,
  };
}
